import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { createProduct, deleteProduct, getCategories, getProducts, getSuppliers, restockProduct, updateProduct } from '../utils/api';
import { loadHtml5Qrcode } from '../utils/html5Qrcode';
import { downloadCsv, openBarcodeSheetPreview } from '../utils/export';
import { fmt } from '../utils/format';
import ProductThumb from '../components/ProductThumb';

const SCANNER_REGION_ID = 'inventory-barcode-reader';
const SUPPORTED_BARCODE_FORMATS = [
  'CODE_128',
  'CODE_39',
  'CODE_93',
  'CODABAR',
  'ITF',
  'EAN_13',
  'EAN_8',
  'UPC_A',
  'UPC_E',
  'UPC_EAN_EXTENSION',
  'RSS_14',
  'RSS_EXPANDED',
  'PDF_417',
  'QR_CODE',
];

function Modal({ open, onClose, title, children, footer, size = '' }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div className={`modal ${size} scale-in`}>
        <div className="modal-head">
          <div className="modal-title">{title}</div>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return <div className="form-group"><label className="form-label">{label}</label>{children}</div>;
}

function generateInternalBarcode() {
  const seed = `${Date.now()}${Math.floor(Math.random() * 1000)}`.slice(-12);
  return `2${seed}`.slice(0, 13);
}

function formatScannerError(error) {
  const message = String(error?.message || error || '').trim();
  const lower = message.toLowerCase();

  if (!message) return 'Unable to start barcode scanner';
  if (lower.includes('permission') || lower.includes('notallowederror')) return 'Camera permission was denied. Allow camera access and try again.';
  if (lower.includes('secure context') || lower.includes('insecure')) return 'Camera scanning requires HTTPS or localhost in this browser.';
  if (lower.includes('no camera') || lower.includes('notfounderror')) return 'No camera was found on this device.';
  if (lower.includes('notreadableerror') || lower.includes('trackstarterror')) return 'The camera is busy in another app or tab. Close other apps using the camera and try again.';
  if (lower.includes('cannot stop')) return 'The scanner had already stopped. Please open it again if you still want to scan.';
  return message;
}

function BarcodeScannerModal({ open, onClose, onDetected }) {
  React.useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    let scanner = null;
    let scannerState = null;
    let startResolved = false;

    async function safeCleanup() {
      if (!scanner) return;

      try {
        const state = typeof scanner.getState === 'function' ? scanner.getState() : null;
        const canStop = state === scannerState?.SCANNING || state === scannerState?.PAUSED;
        if (canStop) await scanner.stop();
      } catch (cleanupError) {
        const message = String(cleanupError?.message || cleanupError || '');
        if (!message.toLowerCase().includes('cannot stop')) {
          console.warn('Inventory scanner cleanup warning:', cleanupError);
        }
      }

      try {
        scanner.clear();
      } catch (clearError) {
        console.warn('Inventory scanner clear warning:', clearError);
      }
    }

    async function startScanner() {
      try {
        const scannerLib = await loadHtml5Qrcode();
        if (cancelled) return;

        const { Html5Qrcode, Html5QrcodeSupportedFormats, Html5QrcodeScannerState } = scannerLib;
        scannerState = Html5QrcodeScannerState;
        scanner = new Html5Qrcode(SCANNER_REGION_ID);

        const cameras = await Html5Qrcode.getCameras();
        if (!cameras.length) throw new Error('No camera found on this device');
        if (cancelled) {
          await safeCleanup();
          return;
        }

        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 12,
            disableFlip: false,
            aspectRatio: 1.777778,
            qrbox: { width: 300, height: 170 },
            formatsToSupport: SUPPORTED_BARCODE_FORMATS.map((key) => Html5QrcodeSupportedFormats[key]).filter((value) => value !== undefined),
          },
          (decodedText) => {
            if (cancelled) return;
            onDetected(decodedText.trim());
            onClose();
          },
          () => {}
        );

        startResolved = true;
        if (cancelled) await safeCleanup();
      } catch (error) {
        if (cancelled) return;

        console.warn('Inventory scanner start failed:', error);
        toast.error(formatScannerError(error));

        if (scanner && !startResolved) await safeCleanup();
        onClose();
      }
    }

    startScanner();

    return () => {
      cancelled = true;
      safeCleanup();
    };
  }, [open, onClose, onDetected]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal modal-sm scale-in">
        <div className="modal-head">
          <div className="modal-title">Scan Existing Product Barcode</div>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>
        <div className="modal-body">
          <div id={SCANNER_REGION_ID} style={{ minHeight: 320, borderRadius: 16, overflow: 'hidden', background: '#000' }} />
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--ink3)' }}>
            Supports common retail and packaging barcodes including EAN-13, EAN-8, UPC, Code 128, Code 39, ITF, ISBN-style book barcodes, and more.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InventoryPage() {
  async function handleImageFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Unable to read image file'));
      reader.readAsDataURL(file);
    });
  }
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showRestock, setShowRestock] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [restockQty, setRestockQty] = useState(1);
  const [scannerOpen, setScannerOpen] = useState(false);
  const qc = useQueryClient();

  const { data: prodData, isLoading } = useQuery({
    queryKey: ['products', { search }],
    queryFn: () => getProducts({ search, limit: 300 }).then((response) => response.data),
    keepPreviousData: true,
  });
  const { data: cats } = useQuery({ queryKey: ['categories'], queryFn: () => getCategories().then((response) => response.data) });
  const { data: sups } = useQuery({ queryKey: ['suppliers'], queryFn: () => getSuppliers().then((response) => response.data) });

  const products = prodData?.products || [];

  const csvRows = useMemo(() => [
    ['Name', 'Barcode', 'Category', 'Price', 'Cost', 'Stock', 'Low stock at', 'Status'],
    ...products.map((product) => [
      product.name,
      product.barcode || '',
      product.category_name || '',
      product.price,
      product.cost,
      product.stock,
      product.low_stock_at,
      product.stock === 0 ? 'Out of Stock' : product.stock <= product.low_stock_at ? 'Low Stock' : 'In Stock',
    ]),
  ], [products]);

  const saveMut = useMutation({
    mutationFn: (payload) => editing ? updateProduct(editing.id, payload).then((response) => response.data) : createProduct(payload).then((response) => response.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      setShowModal(false);
      setEditing(null);
      setForm({});
      toast.success(editing ? 'Product updated' : 'Product added');
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Unable to save product'),
  });

  const delMut = useMutation({
    mutationFn: (id) => deleteProduct(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product removed');
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Unable to remove product'),
  });

  const restockMut = useMutation({
    mutationFn: ({ id, qty }) => restockProduct(id, { qty }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      setShowRestock(null);
      toast.success('Stock updated');
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Unable to update stock'),
  });

  const barcodeMut = useMutation({
    mutationFn: async (product) => {
      const barcode = product.barcode || generateInternalBarcode();
      if (!product.barcode) await updateProduct(product.id, { barcode });
      return { ...product, barcode };
    },
    onSuccess: async (product) => {
      qc.invalidateQueries({ queryKey: ['products'] });
      await openBarcodeSheetPreview({
        title: `${product.name} Label Preview`,
        items: [{ name: product.name, barcode: product.barcode, price: fmt.currency(product.price), unit: product.unit || 'pcs' }],
      });
      toast.success(product.barcode ? 'Barcode label ready for print / PDF' : 'Barcode generated and label ready');
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Unable to prepare barcode label'),
  });

  function openNew() {
    setEditing(null);
    setForm({ image_url: '', stock: 0, low_stock_at: 10, tax_rate: 16, barcode_mode: 'existing' });
    setShowModal(true);
  }

  function openEdit(product) {
    setEditing(product);
    setForm({
      name: product.name,
      barcode: product.barcode,
      barcode_mode: product.barcode ? 'existing' : 'generate',
      description: product.description,
      image_url: product.image_url || '',
      price: product.price,
      cost: product.cost,
      stock: product.stock,
      low_stock_at: product.low_stock_at,
      category_id: product.category_id,
      supplier_id: product.supplier_id,
      unit: product.unit,
      tax_rate: product.tax_rate,
    });
    setShowModal(true);
  }

  function handleDetectedBarcode(scannedBarcode) {
    setForm((value) => ({ ...value, barcode_mode: 'existing', barcode: scannedBarcode }));
    const duplicate = products.find((product) => String(product.barcode || '').trim() === scannedBarcode);

    if (duplicate && (!editing || duplicate.id !== editing.id)) {
      toast.error(`That barcode is already assigned to ${duplicate.name}`);
      return;
    }

    toast.success(`Scanned barcode: ${scannedBarcode}`);
  }

  function handleSubmit() {
    if (!form.name || Number.isNaN(Number(form.price)) || form.price === undefined) {
      toast.error('Name and price are required');
      return;
    }

    const payload = {
      ...form,
      barcode: form.barcode_mode === 'generate' ? (form.barcode || generateInternalBarcode()) : (form.barcode || null),
      image_url: form.image_url?.trim() || null,
      price: Number(form.price),
      cost: Number(form.cost || 0),
      stock: Number(form.stock || 0),
      low_stock_at: Number(form.low_stock_at || 10),
      tax_rate: Number(form.tax_rate || 16),
    };

    delete payload.barcode_mode;
    saveMut.mutate(payload);
  }

  async function exportMissingBarcodeLabels() {
    const targets = products.filter((product) => !product.barcode);
    if (!targets.length) {
      toast.success('All products already have barcodes');
      return;
    }

    try {
      const updatedProducts = [];
      for (const product of targets) {
        const barcode = generateInternalBarcode();
        await updateProduct(product.id, { barcode });
        updatedProducts.push({ ...product, barcode });
      }
      qc.invalidateQueries({ queryKey: ['products'] });
      await openBarcodeSheetPreview({
        title: 'New Product Barcode Labels',
        items: updatedProducts.map((product) => ({ name: product.name, barcode: product.barcode, price: fmt.currency(product.price), unit: product.unit || 'pcs' })),
      });
      toast.success(`${updatedProducts.length} new barcode labels prepared`);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Unable to prepare barcode labels');
    }
  }

  return (
    <div className="p-page fade-up">
      <div className="inventory-toolbar mb-16">
        <div className="search-bar inventory-search">
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search products, brands, or barcodes..." />
        </div>
        <div className="inventory-actions">
          <button className="btn btn-secondary" onClick={() => downloadCsv(`inventory-${new Date().toISOString().slice(0, 10)}.csv`, csvRows)}>Export CSV</button>
          <button className="btn btn-secondary" onClick={exportMissingBarcodeLabels}>Generate Missing Labels</button>
          <button className="btn btn-primary" onClick={openNew}>Add Product</button>
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th><th>Status</th><th>Barcode</th><th>Actions</th></tr></thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></td></tr>
              ) : products.length === 0 ? (
                <tr><td colSpan="7"><div className="empty-state"><div className="empty-title">No products found</div></div></td></tr>
              ) : products.map((product) => {
                const status = product.stock === 0 ? 'badge-red' : product.stock <= product.low_stock_at ? 'badge-amber' : 'badge-green';
                const label = product.stock === 0 ? 'Out of Stock' : product.stock <= product.low_stock_at ? 'Low Stock' : 'In Stock';
                return (
                  <tr key={product.id}>
                    <td>
                      <div className="flex gap-8">
                        <ProductThumb product={product} size="sm" />
                        <div>
                          <div className="td-main">{product.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--ink4)', fontFamily: 'var(--mono)' }}>{fmt.currency(product.price)}</div>
                        </div>
                      </div>
                    </td>
                    <td>{product.category_name ? <span className="badge badge-teal">{product.category_icon} {product.category_name}</span> : '-'}</td>
                    <td className="td-mono">{fmt.currency(product.price)}</td>
                    <td><span className={`mono fw-600 ${product.stock <= product.low_stock_at ? 'dn' : 'up'}`}>{product.stock}</span></td>
                    <td><span className={`badge ${status}`}>{label}</span></td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{product.barcode || 'Not assigned'}</td>
                    <td>
                      <div className="inventory-row-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => { setShowRestock(product); setRestockQty(10); }}>Restock</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(product)}>Edit</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => barcodeMut.mutate(product)} disabled={barcodeMut.isPending}>Label</button>
                        <button className="btn btn-danger btn-sm" onClick={() => { if (window.confirm(`Remove "${product.name}"?`)) delMut.mutate(product.id); }}>Del</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? `Edit: ${editing.name}` : 'Add New Product'}
        footer={(
          <>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saveMut.isPending}>{saveMut.isPending ? 'Saving...' : editing ? 'Save Changes' : 'Add Product'}</button>
          </>
        )}
      >
        <div className="form-grid-2">
          <div style={{ gridColumn: '1 / -1' }}><FormField label="Product Name *"><input className="form-input" value={form.name || ''} onChange={(event) => setForm((value) => ({ ...value, name: event.target.value }))} placeholder="e.g. Coca-Cola 330ml" /></FormField></div>
          <FormField label="Barcode Mode">
            <select className="form-input form-select" value={form.barcode_mode || 'existing'} onChange={(event) => setForm((value) => ({ ...value, barcode_mode: event.target.value, barcode: event.target.value === 'generate' ? (value.barcode || generateInternalBarcode()) : value.barcode }))}>
              <option value="existing">Use existing barcode</option>
              <option value="generate">Generate new label code</option>
            </select>
          </FormField>
          <FormField label="Barcode Value">
            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
              <input className="form-input" value={form.barcode || ''} onChange={(event) => setForm((value) => ({ ...value, barcode: event.target.value }))} placeholder={form.barcode_mode === 'generate' ? 'Auto-generated for print labels' : 'Scan product package or type barcode'} disabled={form.barcode_mode === 'generate'} />
              {form.barcode_mode === 'existing' && <button type="button" className="btn btn-secondary" onClick={() => setScannerOpen(true)}>Scan</button>}
            </div>
            {form.barcode_mode === 'existing' && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ink3)' }}>Use the product's printed barcode. Scan it with the camera or type it manually.</div>}
          </FormField>
          <div style={{ gridColumn: '1 / -1' }}>
            <FormField label="Product Image URL">
              <input className="form-input" value={form.image_url || ''} onChange={(event) => setForm((value) => ({ ...value, image_url: event.target.value }))} placeholder="https://.../product.jpg or upload below" />
            </FormField>
            <FormField label="Upload Product Image">
              <input className="form-input" type="file" accept="image/*" onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                try {
                  const imageDataUrl = await handleImageFile(file);
                  setForm((value) => ({ ...value, image_url: imageDataUrl }));
                  toast.success('Product image added');
                } catch (error) {
                  toast.error(error.message || 'Unable to read image file');
                }
              }} />
            </FormField>
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 12, alignItems: 'center', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: 14 }}>
            <ProductThumb product={{ name: form.name || 'Product preview', image_url: form.image_url }} size="lg" />
            <div>
              <div className="fw-700">Live product preview</div>
              <div className="text-sm text-muted">Use the product image on shelves, POS tiles, receipts, and dashboards instead of emoji placeholders.</div>
            </div>
          </div>
          <FormField label="Category"><select className="form-input form-select" value={form.category_id || ''} onChange={(event) => setForm((value) => ({ ...value, category_id: event.target.value || null }))}><option value="">- Select -</option>{cats?.map((category) => <option key={category.id} value={category.id}>{category.icon} {category.name}</option>)}</select></FormField>
          <FormField label="Supplier"><select className="form-input form-select" value={form.supplier_id || ''} onChange={(event) => setForm((value) => ({ ...value, supplier_id: event.target.value || null }))}><option value="">- Select -</option>{sups?.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}</select></FormField>
          <FormField label="Selling Price (KES) *"><input className="form-input" type="number" min="0" step="0.01" value={form.price ?? ''} onChange={(event) => setForm((value) => ({ ...value, price: event.target.value }))} /></FormField>
          <FormField label="Cost Price (KES)"><input className="form-input" type="number" min="0" step="0.01" value={form.cost ?? ''} onChange={(event) => setForm((value) => ({ ...value, cost: event.target.value }))} /></FormField>
          <FormField label="Stock Qty"><input className="form-input" type="number" min="0" value={form.stock ?? 0} onChange={(event) => setForm((value) => ({ ...value, stock: event.target.value }))} /></FormField>
          <FormField label="Low Stock Alert"><input className="form-input" type="number" min="0" value={form.low_stock_at ?? 10} onChange={(event) => setForm((value) => ({ ...value, low_stock_at: event.target.value }))} /></FormField>
          <FormField label="Unit"><input className="form-input" value={form.unit || 'pcs'} onChange={(event) => setForm((value) => ({ ...value, unit: event.target.value }))} placeholder="pcs, kg, L..." /></FormField>
          <FormField label="VAT Rate %"><input className="form-input" type="number" value={form.tax_rate ?? 16} onChange={(event) => setForm((value) => ({ ...value, tax_rate: event.target.value }))} /></FormField>
          <div style={{ gridColumn: '1 / -1' }}><FormField label="Description"><textarea className="form-input" rows={2} style={{ resize: 'none' }} value={form.description || ''} onChange={(event) => setForm((value) => ({ ...value, description: event.target.value }))} /></FormField></div>
        </div>
      </Modal>

      <Modal
        open={!!showRestock}
        onClose={() => setShowRestock(null)}
        title={`Restock: ${showRestock?.name}`}
        size="modal-sm"
        footer={(
          <>
            <button className="btn btn-secondary" onClick={() => setShowRestock(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={() => restockMut.mutate({ id: showRestock.id, qty: Number(restockQty) })} disabled={restockMut.isPending}>Confirm Restock</button>
          </>
        )}
      >
        <FormField label={`Quantity to add (current: ${showRestock?.stock})`}>
          <input className="form-input" type="number" min="1" value={restockQty} onChange={(event) => setRestockQty(event.target.value)} />
        </FormField>
        <div style={{ background: 'var(--bg2)', borderRadius: 'var(--r-sm)', padding: '10px 14px', fontSize: 13 }}>
          New stock level: <strong>{(showRestock?.stock || 0) + Number(restockQty || 0)}</strong>
        </div>
      </Modal>

      <BarcodeScannerModal open={scannerOpen} onClose={() => setScannerOpen(false)} onDetected={handleDetectedBarcode} />
    </div>
  );
}

